import { complianceEndpoints } from './handlers/exampleCompliance';
import { complianceDocumentEndpoints } from './handlers/compliance';
import { complianceDocGeneratorEndpoints } from './handlers/complianceDocGenerator';
import { authEndpoints } from './handlers/auth';
import { imageEndpoints } from './handlers/images';
import { accessControlEndpoints } from './handlers/accessControl';
import { taskEndpoints } from './handlers/task';
import { genericEndpoints, certificateSupplierMetaEndpoints } from './handlers/genericData';
import { suppliersEndpoints } from './handlers/suppliers';
import { viewsEndpoints } from './handlers/views';
import { t } from '@lingui/macro';
import { VITE_MOCK_ONLY } from 'config';
import type { Server } from 'miragejs';
import { facilitiesEndpoints } from './handlers/facilities';
import {
  supplierAccessEndpoints,
  supplierGuidedOnboardingEndpoints,
} from './handlers/supplierAccess';
import { purchaseOrdersEndpoints } from './handlers/purchaseOrders';
import { purchaseOrdersMappingEndpoints } from './handlers/purchaseOrdersMapping';
import { productCertificatesMappingEndpoints } from './handlers/productCertificatesMapping';
import { itemsEndpoints } from './handlers/items';
import { organizationMigrationEndpoints } from './handlers/organizationMigration';
import { migrationVerificationReportEndpoints } from './handlers/migrationVerificationReport';
import { stylesEndpoints } from './handlers/styles';
import { stylesV2Endpoints } from './handlers/stylesV2';
import { itemsV2Endpoints } from './handlers/itemsV2';
import { sampleRequestsV2Endpoints } from './handlers/sampleRequestsV2';
import { sampleRequestsEndpoints } from './handlers/sampleRequests';
import { organizationIntegrationAccessEndpoints } from './handlers/organizationIntegrationAccess';
import { registerBrandCompaniesEndpoints } from './handlers/brandCompanies';
import { supplierInviteConnectionsEndpoints } from './handlers/supplierInviteConnections';
import { pendingConnectionsEndpoints } from './handlers/pendingConnections';
import { legacyOrgAdminsEndpoints } from './handlers/legacyOrgAdmins';
import { supplierMergeRequestsEndpoints } from './handlers/supplierMergeRequests';
import { fileUploadEndpoints } from './handlers/fileUpload';
import { seedFacilityCertificatesFixture } from 'mocks/data/facilityCertificatesFixture';
import { seedFacilityCertificatesCrudFixture } from 'mocks/data/facilityCertificatesCrudFixture';
import { seedAllCertificatesFadeFixture } from 'mocks/data/allCertificatesFadeFixture';
import { seedFacilityCertificatesLazyLoadFixture } from 'mocks/data/facilityCertificatesLazyLoadFixture';
import { seedFacilityCertificatesEditFixture } from 'mocks/data/facilityCertificatesEditFixture';
import { seedCertificateStatusLifecycleFixture } from 'mocks/data/certificateStatusLifecycleFixture';
import { seedCertificateStatusBrandViewFixture } from 'mocks/data/certificateStatusBrandViewFixture';
import { seedCertificateCreateEpicFixture } from 'mocks/data/certificateCreateEpicFixture';
import { seedFacilityCertificatesDeleteFixture } from 'mocks/data/facilityCertificatesDeleteFixture';
import { seedFacilityCertificatesToggleFixture } from 'mocks/data/facilityCertificatesToggleFixture';
import { seedSubSupplierFiltersFixture } from 'mocks/data/subSupplierFiltersFixture';
import { seedProcessingTypeTagsFixture } from 'mocks/data/processingTypeTagsFixture';
import { seedSubsuppliersSearchFixture } from 'mocks/data/subsuppliersSearchFixture';
import { seedSubsuppliersSortFixture } from 'mocks/data/subsuppliersSortFixture';
import { seedSubsuppliersEdgeCasesFixture } from 'mocks/data/subsuppliersEdgeCasesFixture';
import { seedSubsuppliersPaginationFixture } from 'mocks/data/subsuppliersPaginationFixture';
import { seedSubsuppliersUiUxFixture } from 'mocks/data/subsuppliersUiUxFixture';
import { seedSupplierEditEntryFixture } from 'mocks/data/supplierEditEntryFixture';
import { seedSupplierEditValidationFixture } from 'mocks/data/supplierEditValidationFixture';
import { seedSupplierEditExitFixture } from 'mocks/data/supplierEditExitFixture';
import { HTTP_RESPONSE_CODES } from '../constants';

const enabledEndpoints = (server: Server) => {
  complianceEndpoints(server);
  imageEndpoints(server);
  accessControlEndpoints(server);
};

const disabledEndpoints = (server: Server) => {
  // CDG mocks are VITE_MOCK_ONLY (vitest/playwright): the dev browser hits the
  // live BE for the compliance-document + doc-generator surfaces (DL-61574).
  complianceDocumentEndpoints(server);
  complianceDocGeneratorEndpoints(server);
  authEndpoints(server);
  suppliersEndpoints(server);
  registerBrandCompaniesEndpoints(server);
  // Brand–Supplier pending connections (dl-61067): VITE_MOCK_ONLY only so
  // live DOMAIN is not shadowed when Mirage starts in the normal browser.
  pendingConnectionsEndpoints(server);
  taskEndpoints(server);
  facilitiesEndpoints(server);
  genericEndpoints(server);
  // Certificate modal lookup GETs (scm-certificate-standards / -institutes): the handler was
  // defined in genericData but never registered on the server (DL-63905) — the create-certificate
  // modal's Standard/Institute dropdowns had no mock and failed with a network error in mock mode.
  certificateSupplierMetaEndpoints(server);
  purchaseOrdersEndpoints(server);
  purchaseOrdersMappingEndpoints(server);
  // purchaseOrdersReviewEndpoints is registered internally by purchaseOrdersEndpoints (with the
  // real getPurchaseOrdersData getter it now requires) — do not also register it here, it would
  // just be a redundant duplicate registration with no access to that getter.
  // POST .../facility-process-certificates (assign/remove product certificates on the Step 4 grid,
  // `productCertificatesMapSuppliers` tab) — previously defined but never registered on the server,
  // so the endpoint 404'd in every mock-mode run (DL-63493 epic prerequisite 3 finding). Purely
  // additive: this is the only place that route now exists.
  productCertificatesMappingEndpoints(server);
  supplierAccessEndpoints(server);
  viewsEndpoints(server);
  itemsEndpoints(server);
  // Items "Categories" (`GET /api/itemcategories`) lookup: registered inside `itemsEndpoints`
  // itself (see that handler's own comment) — DL-63357 folded its search-filtering need into the
  // pre-existing DL-62704 handler rather than adding a second `server.get` for the same route.
  organizationMigrationEndpoints(server);
  migrationVerificationReportEndpoints(server);
  stylesEndpoints(server);
  // New-model Styles/Items/Sample-requests v2 prototype (DL-62519) — mock-only, additive.
  stylesV2Endpoints(server);
  itemsV2Endpoints(server);
  sampleRequestsV2Endpoints(server);
  sampleRequestsEndpoints(server);
  organizationIntegrationAccessEndpoints(server);
  // Invite supplier (dl-61766) — VITE_MOCK_ONLY only, never enabledEndpoints.
  supplierInviteConnectionsEndpoints(server);
  supplierGuidedOnboardingEndpoints(server);
  legacyOrgAdminsEndpoints(server);
  supplierMergeRequestsEndpoints(server);
  // Generic legacy file-upload endpoint (DL-63905) — only the facility-certificate upload flow
  // consumes it today; VITE_MOCK_ONLY only, same placement as the other supplier/facility mocks.
  fileUploadEndpoints(server);

  // Deterministic, additive seed for the facility-certificate "inactive row fade" epic
  // (dl-63961). Mock-only; adds one supplier-portal sub-supplier (org 63) with fixed certificates.
  seedFacilityCertificatesFixture();
  // Additive seed for the Supplier Portal add/edit/delete certificate epic (dl-63719): owned
  // facilities (org-scoped route) + a dedicated CRUD sub-supplier (regression route). Runs after
  // the fade fixture so both coexist.
  seedFacilityCertificatesCrudFixture();
  // Additive seed for the "All Certificates" tab inactive-row-fade epic (DL-64859): a dedicated
  // sub-supplier (org 63), isolated from dl-63961's `certfixture` fixture above so the two
  // suites' status mutations never race the same certificate ids.
  seedAllCertificatesFadeFixture();
  // Additive seed for the facility-certificate lazy-load / loading-states / column-mapping /
  // status-badge / list-integrity epic (DL-64870): a dedicated `certlazy` sub-supplier, distinct
  // from the two above, so none of the three epics' fixtures ever contend.
  seedFacilityCertificatesLazyLoadFixture();
  // Additive seed for the "Edit certificate" epic (DL-65076): owned facilities for every
  // story that saves an edit, plus one sub-supplier-owned facility for the prefill/cross-view
  // spine. Runs after the other certificate fixtures so all four coexist.
  seedFacilityCertificatesEditFixture();
  // Additive seed for the certificate five-state status lifecycle e2e epic (DL-65097): a
  // dedicated `certsl` sub-supplier with one facility and five certificates, one per status,
  // distinct from `certfixture` / `certcrud` / `certlazy` so none of the four epics' fixtures
  // ever contend.
  seedCertificateStatusLifecycleFixture();
  // Additive seed for DL-65103 (Brand Certificate View, TC-BV-002): a dedicated `cbv-sub`
  // sub-supplier reached via the brand-side direct-supplier navigation, distinct from the three
  // supplier-portal-session fixtures above so none of the four epics' fixtures ever contend.
  seedCertificateStatusBrandViewFixture();
  // Additive seed for the "Create Certificate" form-validation / expiry-notification / status epic
  // (DL-65060): a dedicated `dl65060sub` sub-supplier + facility, distinct from the three epics
  // above, so none of the four epics' fixtures or row-count assertions ever contend.
  seedCertificateCreateEpicFixture();
  // Additive seed for the "Delete certificate" epic (DL-65088): two facilities under the same
  // `certcrud` sub-supplier — one for the confirm/cancel spine + cross-view stories, one carrying
  // a product certificate for the mapping Step 4 dropdown story.
  seedFacilityCertificatesDeleteFixture();
  // Additive seed for the certificate active/inactive toggle epic (DL-65092): 4 facilities
  // APPENDED to the fade epic's own sub-supplier (`certfixture`) so this epic's specs reuse that
  // epic's navigation/toggle/fade helpers verbatim. Must run AFTER `seedFacilityCertificatesFixture`
  // (it reads-then-appends to the same cache entry, never replacing it).
  seedFacilityCertificatesToggleFixture();
  // Additive seed for the sub-suppliers list "Filters" panel epic (DL-65216): 9 fixed rows
  // appended to the org-2 (brand) supplier cache so filter assertions can rely on known rows
  // instead of counts. Independent of the certificate fixtures above (different cache/org).
  seedSubSupplierFiltersFixture();
  // Additive seed for the sub-suppliers list "Processing Type Display" epic (DL-65232): 3 fixed
  // rows appended to the org-2 (brand) supplier cache with `processingTypes` set directly — no
  // existing factory or fixture sets that field, so the column otherwise renders empty for every
  // row in mock mode. Independent of the DL-65216 filter fixture (different rows, same cache).
  seedProcessingTypeTagsFixture();
  // Additive seed for the Sub-suppliers list "Sorting" epic (DL-65202): a dedicated org (65202,
  // distinct from the sibling table-display/column-selector epics' org 63) with 5 rows whose
  // names are deliberately out of A-Z/Z-A/seed order, so ordering assertions are non-vacuous.
  seedSubsuppliersSortFixture();
  // Additive seed for the Sub-suppliers list "Pagination" epic (DL-65243): 110 marker-tagged rows
  // appended to org 2's cache so the search box can isolate a deterministic, >100-row set that
  // forces a real second cursor page (the default page size is 100).
  seedSubsuppliersPaginationFixture();
  // Additive seed for the Sub-suppliers list "UI/UX" epic (DL-65240): one deterministic row
  // appended to org 2's cache (the brand `page` fixture's own org — same convention as the
  // sibling Filters epic DL-65216) so the search-box story has a guaranteed-unique name to
  // search for among the org's randomised baseline rows.
  seedSubsuppliersUiUxFixture();
  // Additive seed for the Sub-suppliers list "Search Functionality" epic (DL-65208): a dedicated
  // org (65208) with 5 deterministic rows, distinct from every other sub-suppliers epic's org.
  seedSubsuppliersSearchFixture();
  // Additive seed for the Sub-suppliers list "Edge Cases" epic (DL-65253): 4 rows appended to
  // the same org-2 cache the sibling table-display/column-selector/filters/processing-type/
  // pagination/UI-UX/accessibility epics under this Testmo folder all share — a single result,
  // 10+ processing types, a 100+ char name, and a name with special characters.
  seedSubsuppliersEdgeCasesFixture();
  // Supplier Portal "Subsuppliers & Facilities" 3-tab views & counts epic (DL-65332)'s archived-
  // subsupplier fixture is seeded lazily, from `buildAllOrganizationFacilitiesListItems`
  // (`facilitiesOrgList.ts`) on first touch of org 63's facilities — not here. See that call site's
  // comment for why: eager seeding on every test's boot shifted the shared faker draw sequence for
  // unrelated facilities specs.
  // Additive seed for the "Update Supplier: entering edit mode" epic (DL-65988): three
  // deterministic rows appended to org 2's cache (full basic-field + primary/additional contacts,
  // a same-shape row with only a primary contact for the add/edit parity story, and an archived
  // row) — distinct ids from every other sub-suppliers epic's seeded rows.
  seedSupplierEditEntryFixture();
  // Additive seed for the "Update Supplier: edit-form validation & save" epic (DL-65996): one
  // dedicated row per story appended to org 2's cache (distinct ids from every other sub-suppliers
  // epic's seeded rows, including the sibling DL-65988 epic's), plus one deterministic company
  // appended to org 123's cache for the company-suggestion autofill story.
  seedSupplierEditValidationFixture();
  // Additive seed for the "Update Supplier: leaving edit mode and the post-save view" epic
  // (DL-66006): one dedicated row per story appended to org 2's cache (distinct ids from both
  // sibling epics' seeded rows) — the exit-dialog/discard/save/post-save-navigation flows plus
  // two GPS rows (populated/absent) and one row with extra contacts for the sticky-header scroll
  // story.
  seedSupplierEditExitFixture();
};

export const endpoints = (server: Server) => {
  enabledEndpoints(server);
  if (VITE_MOCK_ONLY) {
    disabledEndpoints(server);
  }
};

export { HTTP_RESPONSE_CODES };

export const ERROR_MESSAGES = {
  [HTTP_RESPONSE_CODES.NO_CHANGE]: t`204 No change`,
  [HTTP_RESPONSE_CODES.BAD_INPUT]: t`400 Bad input`,
  [HTTP_RESPONSE_CODES.UNAUTHORIZED]: t`'401 User is not authorised`,
  [HTTP_RESPONSE_CODES.BAD_INPUT]: t`403 Action not allowed`,
  [HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND]: t`404 Entity not found`,
  [HTTP_RESPONSE_CODES.SERVER_ERROR]: t`500 Server error`,
};
